import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Plotly from 'plotly.js-dist-min';

import useUrlState from '../../hooks/useUrlState';
import { useAuth } from '../../auth/AuthProvider';
import { colors, radius, shadows } from '../../styles/theme';
import { formatSayi } from '../../utils/analytics/format';
import { exportCsv } from '../../utils/analytics/export';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const toDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getTodayInputValue = () => toDateInputValue(new Date());

const plotConfig = { displaylogo: false, responsive: true, locale: 'tr' };

// dataviz reference paleti (validate_palette.js ile doğrulandı)
const RENK = {
  teorik: '#2a78d6',   // mavi
  gercek: '#eb6834',   // turuncu
  fark: '#1baf7a',     // aqua
  fire: '#eda100',     // sarı
  sayim: '#4a3aa7',    // mor
  beklenen: '#2a78d6',
  sayilan: '#eb6834',
};

const TuketimAnalizRaporuPage = () => {
  useAuth();

  const [data, setData] = useState(null);
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [kategoriler, setKategoriler] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rebuildLoading, setRebuildLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const [baslangic, setBaslangic] = useUrlState('baslangic', '2026-07-01');
  const [bitis, setBitis] = useUrlState('bitis', getTodayInputValue());
  const [lokasyonId, setLokasyonId] = useUrlState('lokasyon', '');
  const [kategori, setKategori] = useUrlState('kategori', '');

  const pieRef = useRef(null);
  const tuketimRef = useRef(null);
  const sayimRef = useRef(null);

  // Lokasyonlar
  useEffect(() => {
    const fetchLokasyonlar = async () => {
      try {
        const res = await fetch(`${API_URL}/api/lokasyonlar`, { headers: getAuthHeaders() });
        if (res.ok) {
          const result = await res.json();
          setLokasyonlar(result);
          if (result.length > 0 && !lokasyonId) {
            setLokasyonId(String(result[0].id));
          }
        }
      } catch (err) {
        logError('Lokasyonlar yuklenemedi:', err);
      }
    };
    fetchLokasyonlar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Kategoriler
  useEffect(() => {
    fetch(`${API_URL}/api/malzeme-kategorileri`, { headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setKategoriler(Array.isArray(d) ? d : []))
      .catch(() => setKategoriler([]));
  }, []);

  // Tablodan HIZLI okuma — TÜM kategoriler çekilir (üst özet grafikleri lokasyon geneli),
  // kategori filtresi istemci tarafında (alt tablo/detay grafikleri) uygulanır.
  const fetchRapor = useCallback(async () => {
    if (!lokasyonId) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        lokasyon_id: String(lokasyonId),
        baslangic: baslangic || '2026-07-01',
        bitis: bitis || getTodayInputValue(),
        guncelle: 'false',
      });

      const res = await fetch(`${API_URL}/api/raporlar/tuketim-analiz?${params.toString()}`, {
        headers: getAuthHeaders(),
      });

      if (res.ok) {
        setData(await res.json());
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || 'Rapor yuklenemedi');
        setData(null);
      }
    } catch (err) {
      setError('Sunucu hatasi');
      logError('Tuketim analiz raporu yuklenemedi:', err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [lokasyonId, baslangic, bitis]);

  // Tabloyu artımlı güncelle (POST rebuild) → sonra tekrar oku
  const guncelleTablo = useCallback(async () => {
    setRebuildLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/rapor-tuketim-analiz-rebuild`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Tablo güncellenemedi');
      }
      const result = await res.json();
      setSuccess(`Tablo güncellendi (${result.satir ?? 0} satır, ${result.baslangic_gun} → ${result.bitis_gun}).`);
      await fetchRapor();
    } catch (err) {
      setError(err.message || 'Tablo güncellenirken hata oluştu');
      logError('Tuketim analiz tablo guncelleme hatasi:', err);
    } finally {
      setRebuildLoading(false);
    }
  }, [fetchRapor]);

  // Filtre değişince otomatik yükle (kategori hariç — o istemci tarafında filtreler)
  useEffect(() => {
    fetchRapor();
  }, [fetchRapor]);

  const kalemler = useMemo(() => data?.kalemler || [], [data]);

  // Kategori filtresi (istemci tarafı) — alt tablo/detay grafikleri için
  const filteredKalemler = useMemo(
    () => (kategori ? kalemler.filter((k) => (k.kategori || '') === kategori) : kalemler),
    [kalemler, kategori]
  );

  const filteredOzet = useMemo(() => {
    const topla = (f) => filteredKalemler.reduce((a, k) => a + (Number(f(k)) || 0), 0);
    return {
      toplam_satis: topla((k) => k.satis),
      toplam_teorik: topla((k) => k.teorik_tuketim),
      toplam_gercek: topla((k) => k.gercek_tuketim),
      toplam_fark: topla((k) => k.fark),
      toplam_sayim_farki: filteredKalemler.reduce(
        (a, k) => a + (k.sayim_farki != null ? Number(k.sayim_farki) : 0),
        0
      ),
    };
  }, [filteredKalemler]);

  // ---- ÜST GRAFİK 1: Pasta — Gerçek Tüketim (%50) vs Teorik + Fire + Sayım Farkı (%50) ----
  const pieGrafik = useMemo(() => {
    const oz = data?.ozet || {};
    const G = Number(oz.toplam_gercek || 0);
    const teorikBase = Math.max(0, Number(oz.toplam_satis || 0));            // firesiz baz
    const fire = Math.max(0, Number(oz.toplam_teorik || 0) - Number(oz.toplam_satis || 0)); // teorik - satış
    const sayimHam = Number(oz.toplam_sayim_farki || 0);
    const absSayim = Math.abs(sayimHam);

    const sum2 = teorikBase + fire + absSayim;      // "diğer yarı" toplamı
    const gercekGeo = sum2 > 0 ? sum2 : (G > 0 ? G : 1); // Gerçek dilimini %50'ye sabitle

    return {
      ok: sum2 > 0 || G > 0,
      data: [
        {
          type: 'pie',
          labels: ['Gerçek Tüketim', 'Teorik (Satış)', 'Fire', 'Sayım Farkı'],
          // Geometri: Gerçek = diğer üçünün toplamı → pastanın tam %50'si
          values: [gercekGeo, teorikBase, fire, absSayim],
          // Etiket/hover'da GERÇEK değerler gösterilir (Sayım Farkı işaretiyle)
          customdata: [G, teorikBase, fire, sayimHam],
          marker: {
            colors: [RENK.gercek, RENK.teorik, RENK.fire, RENK.sayim],
            line: { color: '#ffffff', width: 2 },
          },
          sort: false,
          direction: 'clockwise',
          rotation: 0,
          hole: 0.4,
          // Dilim üzerinde YÜZDE (BEYAZ); gerçek değer + yüzde hover'da (BEYAZ yazı)
          texttemplate: '%{percent}',
          textposition: 'inside',
          insidetextorientation: 'horizontal',
          hovertemplate: '%{label}<br>Değer: %{customdata:.2f}<br>Pay: %{percent}<extra></extra>',
          textfont: { size: 13, color: '#ffffff' },
          insidetextfont: { size: 13, color: '#ffffff' },
          outsidetextfont: { size: 13, color: '#ffffff' },
          hoverlabel: { font: { color: '#ffffff', size: 12 }, bgcolor: '#334155', bordercolor: '#334155' },
        },
      ],
      layout: {
        height: 360,
        // Sağdaki dikey legend için sağ boşluk açıyoruz
        margin: { l: 10, r: 140, t: 10, b: 10 },
        paper_bgcolor: 'rgba(0,0,0,0)',
        showlegend: true,
        // Legend SAĞ tarafta, dikey ve ortalanmış
        legend: { orientation: 'v', x: 1.02, y: 0.5, xanchor: 'left', yanchor: 'middle' },
        font: { family: 'system-ui, -apple-system, "Segoe UI", sans-serif', color: '#52514e', size: 12 },
      },
    };
  }, [data]);

  // ---- ALT GRAFİK: Teorik / Gerçek Tüketim / Fark (kategori filtreli) ----
  const tuketimGrafik = useMemo(() => {
    const x = filteredKalemler.map((k) => k.malzeme_ad);
    return {
      data: [
        {
          type: 'bar',
          name: 'Teorik Tüketim',
          x,
          y: filteredKalemler.map((k) => k.teorik_tuketim),
          marker: { color: RENK.teorik },
          customdata: filteredKalemler.map((k) => k.birim || ''),
          hovertemplate: '<b>%{x}</b><br>Teorik Tüketim: %{y:.2f} %{customdata}<extra></extra>',
        },
        {
          type: 'bar',
          name: 'Gerçek Tüketim (Depo)',
          x,
          y: filteredKalemler.map((k) => k.gercek_tuketim),
          marker: { color: RENK.gercek },
          customdata: filteredKalemler.map((k) => k.birim || ''),
          hovertemplate: '<b>%{x}</b><br>Gerçek Tüketim: %{y:.2f} %{customdata}<extra></extra>',
        },
        {
          type: 'bar',
          name: 'Fark',
          x,
          y: filteredKalemler.map((k) => k.fark),
          marker: { color: RENK.fark },
          customdata: filteredKalemler.map((k) => k.birim || ''),
          hovertemplate: '<b>%{x}</b><br>Fark: %{y:.2f} %{customdata}<extra></extra>',
        },
      ],
      layout: {
        barmode: 'group',
        bargap: 0.25,
        bargroupgap: 0.08,
        height: 430,
        margin: { l: 60, r: 20, t: 10, b: 90 },
        paper_bgcolor: 'rgba(0,0,0,0)',
        plot_bgcolor: 'rgba(0,0,0,0)',
        font: { family: 'system-ui, -apple-system, "Segoe UI", sans-serif', color: '#52514e', size: 12 },
        legend: { orientation: 'h', y: 1.12, x: 0 },
        xaxis: { tickangle: -40, automargin: true, tickfont: { size: 11 } },
        yaxis: { title: { text: 'Miktar' }, gridcolor: '#e1e0d9', zerolinecolor: '#c3c2b7' },
      },
    };
  }, [filteredKalemler]);

  // ---- ALT GRAFİK: Beklenen Stok vs Sayılan Stok (kategori filtreli) ----
  const sayimKalemleri = useMemo(
    () => filteredKalemler.filter((k) => k.sayilan_stok !== null && k.sayilan_stok !== undefined),
    [filteredKalemler]
  );

  const sayimGrafik = useMemo(() => {
    const x = sayimKalemleri.map((k) => k.malzeme_ad);
    return {
      data: [
        {
          type: 'bar',
          name: 'Beklenen Stok (Depoda olmalı)',
          x,
          y: sayimKalemleri.map((k) => k.beklenen_stok),
          marker: { color: RENK.beklenen },
          hovertemplate: '<b>%{x}</b><br>Beklenen: %{y:.2f}<extra></extra>',
        },
        {
          type: 'bar',
          name: 'Sayılan Stok (Fiili)',
          x,
          y: sayimKalemleri.map((k) => k.sayilan_stok),
          marker: { color: RENK.sayilan },
          hovertemplate: '<b>%{x}</b><br>Sayılan: %{y:.2f}<extra></extra>',
        },
        {
          type: 'scatter',
          mode: 'markers',
          name: 'Sayım Farkı',
          x,
          y: sayimKalemleri.map((k) => k.sayim_farki),
          marker: { color: RENK.sayim, size: 9, symbol: 'diamond' },
          hovertemplate: '<b>%{x}</b><br>Sayım Farkı: %{y:.2f}<extra></extra>',
        },
      ],
      layout: {
        barmode: 'group',
        bargap: 0.25,
        bargroupgap: 0.08,
        height: 430,
        margin: { l: 60, r: 20, t: 10, b: 90 },
        paper_bgcolor: 'rgba(0,0,0,0)',
        plot_bgcolor: 'rgba(0,0,0,0)',
        font: { family: 'system-ui, -apple-system, "Segoe UI", sans-serif', color: '#52514e', size: 12 },
        legend: { orientation: 'h', y: 1.12, x: 0 },
        xaxis: { tickangle: -40, automargin: true, tickfont: { size: 11 } },
        yaxis: { title: { text: 'Stok' }, gridcolor: '#e1e0d9', zerolinecolor: '#c3c2b7' },
      },
    };
  }, [sayimKalemleri]);

  // Plotly çizimleri (plotly.js-dist-min imperatif API)
  // Not: Plotly.react mevcut grafiği yerinde günceller; her değişimde purge ÇAĞIRMIYORUZ.
  // (StrictMode'da mount→unmount→mount sırasında purge, autoMargin yeniden-çizimiyle
  //  yarışıp "_redrawFromAutoMarginCount undefined" hatasına yol açıyordu.)
  useEffect(() => {
    const el = pieRef.current;
    if (!el || !pieGrafik.ok) return;
    Plotly.react(el, pieGrafik.data, pieGrafik.layout, plotConfig);
  }, [pieGrafik]);

  useEffect(() => {
    const el = tuketimRef.current;
    if (!el) return;
    Plotly.react(el, tuketimGrafik.data, tuketimGrafik.layout, plotConfig);
  }, [tuketimGrafik]);

  useEffect(() => {
    const el = sayimRef.current;
    if (!el) return;
    Plotly.react(el, sayimGrafik.data, sayimGrafik.layout, plotConfig);
  }, [sayimGrafik]);

  // ---- Stiller (TuketimRaporuPage ile tutarlı) ----
  const containerStyle = { padding: '24px', maxWidth: '1200px', margin: '0 auto' };
  const cardStyle = {
    background: colors.surface,
    borderRadius: radius.lg,
    padding: '24px',
    boxShadow: shadows.sm,
    marginBottom: '20px',
  };
  const filterRowStyle = { display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '20px' };
  const fieldStyle = { display: 'flex', flexDirection: 'column', gap: '4px' };
  const labelStyle = { fontSize: '13px', fontWeight: '600', color: '#555' };
  const inputStyle = { padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.md, fontSize: '14px', outline: 'none' };
  const btnStyle = { padding: '8px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: radius.md, fontSize: '14px', fontWeight: '600', cursor: 'pointer' };
  const thStyle = { padding: '10px 12px', textAlign: 'left', fontWeight: '600', fontSize: '13px', color: '#555', borderBottom: `2px solid ${colors.border}`, position: 'sticky', top: 0, background: colors.surface, whiteSpace: 'nowrap' };
  const thRightStyle = { ...thStyle, textAlign: 'right' };
  const tdStyle = { padding: '8px 12px', fontSize: '13px', borderBottom: '1px solid #f3f3f3' };
  const tdRightStyle = { ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
  const chartBoxStyle = { padding: '16px', border: `1px solid ${colors.border}`, borderRadius: radius.md, background: '#fff' };
  const ikiGrafikGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '16px', marginBottom: '20px' };

  const ozetKutu = (renk, bg, baslik, deger) => (
    <div style={{ padding: '10px 18px', background: bg, borderRadius: radius.md, fontSize: '14px', color: renk, fontWeight: '600' }}>
      <div style={{ fontSize: '11px', opacity: 0.8 }}>{baslik}</div>
      {formatSayi(deger)}
    </div>
  );

  const handleCsv = () => {
    exportCsv(
      filteredKalemler.map((k) => ({
        Malzeme: k.malzeme_ad,
        Kod: k.kod || '',
        Birim: k.birim || '',
        Kategori: k.kategori || '',
        'Satış': k.satis,
        'Fire %': k.fire_yuzde,
        'Gerçek Tüketim (Depo)': k.gercek_tuketim,
        'Teorik Tüketim': k.teorik_tuketim,
        'Fark': k.fark,
        'Dönem Başı Sayım': k.donem_basi_sayim,
        'Dönem İçi Giriş': k.donem_ici_giris,
        'Beklenen Stok': k.beklenen_stok,
        'Sayılan Stok': k.sayilan_stok,
        'Sayım Farkı': k.sayim_farki,
      })),
      `tuketim-analiz-${baslangic}-${bitis}.csv`
    );
  };

  const varMi = data && kalemler.length > 0;

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <h2 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#1e293b' }}>
          Tüketim Analiz Raporu — Satış / Fire / Teorik / Gerçek / Fark
        </h2>

        {/* 1) Lokasyon + Tarih aralığı */}
        <div style={filterRowStyle}>
          <div style={fieldStyle}>
            <label style={labelStyle}>Lokasyon</label>
            <select style={{ ...inputStyle, minWidth: '180px' }} value={lokasyonId} onChange={(e) => setLokasyonId(e.target.value)}>
              <option value="">Lokasyon secin</option>
              {lokasyonlar.map((l) => (
                <option key={l.id} value={l.id}>{l.ad}</option>
              ))}
            </select>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Başlangıç</label>
            <input type="date" style={inputStyle} value={baslangic} onChange={(e) => setBaslangic(e.target.value)} />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Bitiş</label>
            <input type="date" style={inputStyle} value={bitis} onChange={(e) => setBitis(e.target.value)} />
          </div>
        </div>

        {/* 2) Üst özet grafiği (lokasyon geneli — kategori filtresinden bağımsız) */}
        {varMi && (
          <>
            <div style={{ padding: '10px 18px', background: '#f0f9ff', borderRadius: radius.md, fontSize: '14px', color: '#1e40af', fontWeight: '600', marginBottom: '12px', display: 'inline-block' }}>
              {data.lokasyon_ad || 'Lokasyon'} &mdash; {data.baslangic} / {data.bitis}
            </div>

            <div style={ikiGrafikGrid}>
              <div style={chartBoxStyle}>
                <h3 style={{ margin: '0 0 8px', fontSize: '15px', color: '#1e293b' }}>
                  Toplam Tüketim : Gerçek vs Teorik + Fire + Sayım Farkı
                </h3>
                {pieGrafik.ok ? (
                  <div ref={pieRef} style={{ width: '100%' }} />
                ) : (
                  <div style={{ padding: '24px', color: '#64748b' }}>Grafik için yeterli veri yok.</div>
                )}
              </div>
            </div>
          </>
        )}

        {/* 3) Kategori + Listele + Tabloyu Güncelle */}
        <div style={filterRowStyle}>
          <div style={fieldStyle}>
            <label style={labelStyle}>Kategori</label>
            <select style={{ ...inputStyle, minWidth: '160px' }} value={kategori} onChange={(e) => setKategori(e.target.value)}>
              <option value="">Tumu</option>
              {kategoriler.map((k) => (
                <option key={k} value={k}>{k}</option>
              ))}
            </select>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>&nbsp;</label>
            <button style={{ ...btnStyle, opacity: loading || !lokasyonId ? 0.6 : 1 }} onClick={fetchRapor} disabled={loading || !lokasyonId}>
              {loading ? 'Yukleniyor...' : 'Listele'}
            </button>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>&nbsp;</label>
            <button
              style={{
                ...btnStyle,
                background: '#16a34a',
                opacity: rebuildLoading ? 0.6 : 1,
                cursor: rebuildLoading ? 'not-allowed' : 'pointer',
              }}
              onClick={guncelleTablo}
              disabled={rebuildLoading}
              title="Analiz tablosunu kaldığı günden itibaren yeniden hesaplar (bugünü tazeler)"
            >
              {rebuildLoading ? 'Hesaplaniyor...' : 'Tabloyu Güncelle'}
            </button>
          </div>
        </div>

        {error && (
          <div style={{ padding: '12px', background: '#fef2f2', color: '#dc2626', borderRadius: radius.md, marginBottom: '16px', fontSize: '14px' }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{ padding: '12px', background: '#f0fdf4', color: '#166534', borderRadius: radius.md, marginBottom: '16px', fontSize: '14px' }}>
            {success}
          </div>
        )}

        {/* 4) Alt: özet kutular + detay grafikleri + tablo (kategori filtreli) */}
        {data && kalemler.length === 0 && (
          <div style={{ padding: '24px', color: '#64748b' }}>
            Bu dönem için tabloda veri yok. Analiz tablosunu ilk kez oluşturmak veya güncellemek için
            {' '}<b>“Tabloyu Güncelle”</b> butonuna basın.
          </div>
        )}

        {varMi && (
          <>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
              {ozetKutu('#1e40af', '#f0f9ff', 'Toplam Teorik', filteredOzet.toplam_teorik)}
              {ozetKutu('#9a3412', '#fff7ed', 'Toplam Gerçek', filteredOzet.toplam_gercek)}
              {ozetKutu('#065f46', '#f0fdf4', 'Toplam Fark', filteredOzet.toplam_fark)}
              {ozetKutu('#4a3aa7', '#f5f3ff', 'Toplam Sayım Farkı', filteredOzet.toplam_sayim_farki)}
              <button
                onClick={handleCsv}
                style={{ marginLeft: 'auto', padding: '8px 16px', border: '1px solid #2563eb', borderRadius: radius.md, background: '#fff', color: '#2563eb', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}
              >
                CSV Indir
              </button>
            </div>

            {filteredKalemler.length === 0 ? (
              <div style={{ padding: '24px', color: '#64748b' }}>Bu kategoride veri bulunamadı.</div>
            ) : (
              <>
                <h3 style={{ margin: '10px 0 4px', fontSize: '15px', color: '#1e293b' }}>Teorik vs Gerçek Tüketim ve Fark</h3>
                <div style={{ ...chartBoxStyle, marginBottom: '18px' }}>
                  <div ref={tuketimRef} style={{ width: '100%' }} />
                </div>

                {sayimKalemleri.length > 0 && (
                  <>
                    <h3 style={{ margin: '10px 0 4px', fontSize: '15px', color: '#1e293b' }}>
                      Sayım Farkı — Depoda olması gereken (Beklenen) vs Sayımda çıkan (Fiili)
                    </h3>
                    <div style={{ ...chartBoxStyle, marginBottom: '18px' }}>
                      <div ref={sayimRef} style={{ width: '100%' }} />
                    </div>
                  </>
                )}

                <div style={{ overflowX: 'auto', maxHeight: '600px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Malzeme</th>
                        <th style={thStyle}>Kod</th>
                        <th style={thStyle}>Birim</th>
                        <th style={thStyle}>Kategori</th>
                        <th style={thRightStyle}>Satış</th>
                        <th style={thRightStyle}>Fire %</th>
                        <th style={thRightStyle}>Gerçek Tük. (Depo)</th>
                        <th style={thRightStyle}>Teorik Tük.</th>
                        <th style={thRightStyle}>Fark</th>
                        <th style={thRightStyle}>Beklenen Stok</th>
                        <th style={thRightStyle}>Sayılan</th>
                        <th style={thRightStyle}>Sayım Farkı</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredKalemler.map((k, i) => {
                        const farkColor = k.fark > 0 ? '#dc2626' : k.fark < 0 ? '#16a34a' : '#1e293b';
                        const sayimColor =
                          k.sayim_farki == null ? '#94a3b8' : Math.abs(k.sayim_farki) < 1e-9 ? '#16a34a' : '#dc2626';
                        return (
                          <tr key={k.malzeme_id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                            <td style={{ ...tdStyle, fontWeight: '500' }}>{k.malzeme_ad}</td>
                            <td style={{ ...tdStyle, color: '#888', fontSize: '12px' }}>{k.malzeme_kod || k.kod}</td>
                            <td style={tdStyle}>{k.birim}</td>
                            <td style={tdStyle}>
                              <span style={{ padding: '2px 8px', borderRadius: radius.sm, background: '#f1f5f9', fontSize: '11px', fontWeight: '500' }}>
                                {k.kategori}
                              </span>
                            </td>
                            <td style={tdRightStyle}>{formatSayi(k.satis)}</td>
                            <td style={tdRightStyle}>{formatSayi(k.fire_yuzde)}</td>
                            <td style={{ ...tdRightStyle, color: '#9a3412', fontWeight: '600' }}>{formatSayi(k.gercek_tuketim)}</td>
                            <td style={{ ...tdRightStyle, color: '#1e40af' }}>{formatSayi(k.teorik_tuketim)}</td>
                            <td style={{ ...tdRightStyle, fontWeight: '700', color: farkColor }}>{formatSayi(k.fark)}</td>
                            <td style={tdRightStyle}>{formatSayi(k.beklenen_stok)}</td>
                            <td style={tdRightStyle}>{k.sayilan_stok == null ? '—' : formatSayi(k.sayilan_stok)}</td>
                            <td style={{ ...tdRightStyle, fontWeight: '700', color: sayimColor }}>
                              {k.sayim_farki == null ? '—' : formatSayi(k.sayim_farki)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TuketimAnalizRaporuPage;
